/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */

import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useEffect, useState } from 'react';
import {
  applyConsentPreferences,
  CONSENT_STORAGE_KEY,
  ConsentPreferences,
  readStoredConsent,
  updateConsentPreferences,
} from '../lib/utils/consent';

const primaryButtonClasses =
  'rounded-full border border-primary bg-primary px-5 py-2 text-sm font-primarybold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
const secondaryButtonClasses =
  'rounded-full border border-primary bg-white px-5 py-2 text-sm font-primarybold text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';

const CookieConsent: React.FC = function CookieConsent() {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(() => readStoredConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [statistics, setStatistics] = useState(preferences?.statistics ?? false);
  const [marketing, setMarketing] = useState(preferences?.marketing ?? false);

  const syncDraftWithPreferences = (nextPreferences: ConsentPreferences | null) => {
    setStatistics(nextPreferences?.statistics ?? false);
    setMarketing(nextPreferences?.marketing ?? false);
  };

  const openPreferences = () => {
    syncDraftWithPreferences(preferences);
    setPreferencesOpen(true);
  };

  const closePreferences = () => {
    syncDraftWithPreferences(preferences);
    setPreferencesOpen(false);
  };

  const saveChoice = (statisticsConsent: boolean, marketingConsent: boolean) => {
    const nextPreferences = updateConsentPreferences({
      statistics: statisticsConsent,
      marketing: marketingConsent,
    });

    setPreferences(nextPreferences);
    syncDraftWithPreferences(nextPreferences);
    setPreferencesOpen(false);
  };

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CONSENT_STORAGE_KEY && event.key !== null) return;

      const storedPreferences = readStoredConsent();

      if (storedPreferences) {
        applyConsentPreferences(storedPreferences);
        setPreferences(storedPreferences);
        syncDraftWithPreferences(storedPreferences);
      } else {
        applyConsentPreferences({ statistics: false, marketing: false });
        setPreferences(null);
        syncDraftWithPreferences(null);
      }

      setPreferencesOpen(false);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <>
      {!preferences && !preferencesOpen && (
        <section
          aria-label="Consenso cookie"
          className="fixed inset-x-4 bottom-4 z-40 mx-auto max-w-4xl rounded-xl border border-lightgrey bg-white p-5 shadow-card"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-primarybold text-lg text-black">La tua privacy su HR Meccatronica</h2>
              <p className="mt-2 text-sm text-textgrey">
                Utilizziamo strumenti necessari al funzionamento della piattaforma e, solo con il tuo consenso,
                strumenti statistici e di marketing. Puoi modificare la scelta in qualsiasi momento.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" className={primaryButtonClasses} onClick={() => saveChoice(true, true)}>
                Accetta tutto
              </button>
              <button type="button" className={secondaryButtonClasses} onClick={() => saveChoice(false, false)}>
                Rifiuta non necessari
              </button>
              <button type="button" className={secondaryButtonClasses} onClick={openPreferences}>
                Personalizza
              </button>
            </div>
          </div>
        </section>
      )}

      {preferences && !preferencesOpen && (
        <button
          type="button"
          className="fixed bottom-3 left-3 z-30 rounded-full border border-primary bg-white px-4 py-2 text-xs font-primarybold text-primary shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          onClick={openPreferences}
        >
          Preferenze cookie
        </button>
      )}

      <Transition appear show={preferencesOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePreferences}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
                  <Dialog.Title as="h2" className="font-primarybold text-xl text-primary">
                    Preferenze cookie
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 text-sm text-textgrey">
                    Scegli quali categorie consentire. Gli strumenti necessari sono sempre attivi per garantire il
                    funzionamento della piattaforma.
                  </Dialog.Description>

                  <div className="mt-6 space-y-4">
                    <label
                      htmlFor="consent-necessary"
                      className="flex items-center justify-between rounded-lg border border-lightgrey p-4"
                    >
                      <span>
                        <span className="block font-primarybold text-black">Necessari</span>
                        <span className="block text-xs text-textgrey">Autenticazione e funzionalità essenziali.</span>
                      </span>
                      <input
                        id="consent-necessary"
                        type="checkbox"
                        checked
                        disabled
                        className="h-5 w-5 accent-primary"
                      />
                    </label>

                    <label
                      htmlFor="consent-statistics"
                      className="flex cursor-pointer items-center justify-between rounded-lg border border-lightgrey p-4"
                    >
                      <span>
                        <span className="block font-primarybold text-black">Statistiche</span>
                        <span className="block text-xs text-textgrey">
                          Misurazione dell’utilizzo della piattaforma.
                        </span>
                      </span>
                      <input
                        id="consent-statistics"
                        type="checkbox"
                        checked={statistics}
                        onChange={(event) => setStatistics(event.target.checked)}
                        className="h-5 w-5 accent-primary"
                      />
                    </label>

                    <label
                      htmlFor="consent-marketing"
                      className="flex cursor-pointer items-center justify-between rounded-lg border border-lightgrey p-4"
                    >
                      <span>
                        <span className="block font-primarybold text-black">Marketing</span>
                        <span className="block text-xs text-textgrey">
                          Misurazione delle campagne e contenuti promozionali.
                        </span>
                      </span>
                      <input
                        id="consent-marketing"
                        type="checkbox"
                        checked={marketing}
                        onChange={(event) => setMarketing(event.target.checked)}
                        className="h-5 w-5 accent-primary"
                      />
                    </label>
                  </div>

                  <div className="mt-6 flex flex-wrap justify-end gap-3">
                    <button type="button" className={secondaryButtonClasses} onClick={closePreferences}>
                      Annulla
                    </button>
                    <button
                      type="button"
                      className={primaryButtonClasses}
                      onClick={() => saveChoice(statistics, marketing)}
                    >
                      Salva preferenze
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CookieConsent;
